import type { SidebarRowLayout } from "@/components/navigation/project-nav-sidebar-layout";

// Version 1 layouts were arranged against the category sidebars before Data joined the rail. Rows
// keep their ids across versions, but some sections were renamed, moved to another category, or
// removed, and a stored layout's references to those would otherwise be dropped. These tables are
// a frozen record of that change; later edits to the default sidebars must not touch them.

/** Version 1 sections that live on under another id, possibly in another category. */
const V1_SECTION_SUCCESSORS: ReadonlyMap<string, string> = new Map([
  ["compute:Containers", "compute:Workloads"],
  ["compute:Queries", "catalog:queries-nav"],
  ["compute:Operations", "more:Jobs"],
  ["catalog:outputs", "data:outputs"],
  ["catalog:integrations", "data:integrations"],
  ["configuration:Storage", "data:integrations"],
]);

/**
 * Version 1 sections that share their successor with another one, which keeps its title. Their
 * rows' saved order follows that section's.
 */
const V1_JOINING_SECTIONS: ReadonlySet<string> = new Set(["configuration:Storage"]);

/**
 * Version 1 sections whose rows the reader put there stay in a section of the reader's own under
 * the same title: the ones version 2 removed, and Settings' Storage, which moved to Data although
 * Settings keeps its rows to itself.
 */
const V1_KEPT_SECTION_LABELS: ReadonlyMap<string, string> = new Map([
  ["agents:agent-telemetry-nav", "Telemetry"],
  ["configuration:Storage", "Storage"],
]);

/**
 * Rows whose default category version 2 changed, by that category. Vector store and Buckets also
 * moved out of Settings, to Data and More, but Settings rows cannot change category, so no
 * version 1 layout put them anywhere they could still go.
 */
const V2_ROW_CATEGORIES: ReadonlyMap<string, string> = new Map([
  ["explorer-sql", "data"],
  ["online-queries", "catalog"],
  ["offline-queries", "catalog"],
  ["script-tasks", "more"],
  ["workflows", "more"],
  ["build-workflows", "more"],
  ["data_sources", "data"],
  ["online-store", "data"],
  ["offline-store", "data"],
  ["datasets", "data"],
  ["storage", "data"],
  ["ai-traces", "observability"],
]);

/** Section ids, default or the reader's, start with their category's id. */
const sectionCategory = (sectionId: string) => sectionId.slice(0, sectionId.indexOf(":"));

const successorOf = (sectionId: string) => V1_SECTION_SUCCESSORS.get(sectionId) ?? sectionId;

const renameSectionLabels = (labels: Record<string, string>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(labels).flatMap(([sectionId, label]) =>
      V1_JOINING_SECTIONS.has(sectionId) ? [] : [[successorOf(sectionId), label]],
    ),
  );

const renameSectionOrders = (orders: Record<string, string[]>): Record<string, string[]> => {
  const renamed: Record<string, string[]> = {};
  for (const [sectionId, order] of Object.entries(orders)) {
    if (!Array.isArray(order)) continue;
    const successor = successorOf(sectionId);
    const other = renamed[successor] ?? [];
    renamed[successor] = V1_JOINING_SECTIONS.has(sectionId)
      ? [...other, ...order]
      : [...order, ...other];
  }
  return renamed;
};

/**
 * A version 1 row layout rewritten for version 2, so what the reader arranged stays put: section
 * titles and row orders follow their section to its new id, and a row the reader put in a section
 * follows it too, into another category if the section moved (see `V1_KEPT_SECTION_LABELS` for
 * the exceptions). Rows the reader left in their default places take their version 2 places.
 */
export const migrateV1SidebarRowLayout = (layout: SidebarRowLayout): SidebarRowLayout => {
  const orderBySection = renameSectionOrders(layout.orderBySection);
  const labelBySection = renameSectionLabels(layout.labelBySection);
  const addedSectionIdsByCategory = { ...layout.addedSectionIdsByCategory };
  const categoryByRow = { ...layout.categoryByRow };
  const sectionByRow: Record<string, string> = {};

  for (const [rowId, sectionId] of Object.entries(layout.sectionByRow)) {
    if (typeof sectionId !== "string") continue;
    const keptLabel = V1_KEPT_SECTION_LABELS.get(sectionId);
    const destination = keptLabel != null ? sectionId : successorOf(sectionId);
    const categoryId = sectionCategory(destination);
    sectionByRow[rowId] = destination;

    if (keptLabel != null) {
      const added = addedSectionIdsByCategory[categoryId];
      const addedIds = Array.isArray(added) ? added : [];
      if (!addedIds.includes(destination)) {
        addedSectionIdsByCategory[categoryId] = [...addedIds, destination];
      }
      labelBySection[destination] = layout.labelBySection[sectionId] ?? keptLabel;
      orderBySection[destination] = layout.orderBySection[sectionId] ?? [];
    }
    // A version 1 row sits in the category of the section it was put in, which version 2 would
    // otherwise replace with the row's own category.
    const defaultCategory =
      categoryByRow[rowId] ?? V2_ROW_CATEGORIES.get(rowId) ?? sectionCategory(sectionId);
    if (categoryId !== defaultCategory) categoryByRow[rowId] = categoryId;
  }

  return {
    ...layout,
    orderBySection,
    labelBySection,
    addedSectionIdsByCategory,
    categoryByRow,
    sectionByRow,
    // Deleting a section hides it only while it has no rows, in its own category; that says
    // nothing about a section that has since left for another one.
    deletedSectionIds: layout.deletedSectionIds.flatMap((sectionId) => {
      const successor = successorOf(sectionId);
      return V1_KEPT_SECTION_LABELS.has(sectionId) ||
        sectionCategory(successor) !== sectionCategory(sectionId)
        ? []
        : [successor];
    }),
  };
};

/** A version 1 category's section order rewritten for version 2, keeping each section's place. */
export const migrateV1SectionOrder = (categoryId: string, order: readonly string[]): string[] => {
  const migrated = order.map((sectionId) => {
    const successor = successorOf(sectionId);
    return sectionCategory(successor) === categoryId ? successor : sectionId;
  });
  return migrated.filter((sectionId, index) => migrated.indexOf(sectionId) === index);
};
